/* eslint-disable */
import { useMemo } from 'react';
import { Store, Package, ScanLine, Hash } from 'lucide-react';

/**
 * Step 4 — confirm what is about to be created.
 *
 * Leads with the numbers that decide whether this is right: how many audits,
 * at how many stores, over how many asset types. Confirm creates one audit per
 * store and each one freezes its expected quantities on the spot, so those
 * counts are the headline rather than a footnote.
 */

const UNCATEGORISED = 'Uncategorised';

const StatTile = ({ icon: Icon, value, label, tone = 'slate' }) => {
	const tones = {
		slate: 'bg-slate-50 text-slate-700 border-slate-200',
		teal: 'bg-teal-50 text-teal-800 border-teal-200',
	};
	return (
		<div
			className={`flex items-center flex-1 gap-3 px-4 py-3 border rounded-lg ${tones[tone]}`}
		>
			<Icon className='opacity-70 size-5 shrink-0' />
			<div className='flex flex-col leading-tight'>
				<span className='text-xl font-semibold'>{value}</span>
				<span className='text-xs opacity-80'>{label}</span>
			</div>
		</div>
	);
};

const AssetAuditStep4 = ({
	auditName,
	selectedStores = [],
	selectedAssetTypes = [],
}) => {
	const scannedCount = selectedAssetTypes.filter((type) => type?.traceable).length;
	const countedCount = selectedAssetTypes.length - scannedCount;

	// Same grouping as the picker, so the verify screen reads like the screen
	// the choices were made on.
	const groupedTypes = useMemo(() => {
		const byCategory = new Map();
		selectedAssetTypes.forEach((type) => {
			const key = type?.categoryName || UNCATEGORISED;
			if (!byCategory.has(key)) byCategory.set(key, []);
			byCategory.get(key).push(type);
		});
		return [...byCategory.entries()]
			.sort((a, b) => a[0].localeCompare(b[0]))
			.map(([name, types]) => ({
				name,
				types: types.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? '')),
			}));
	}, [selectedAssetTypes]);

	return (
		<div className='flex flex-col h-full min-h-0 gap-4 p-1'>
			<div className='flex items-baseline gap-3'>
				<span className='text-xs tracking-wide text-gray-500 uppercase'>
					Audit Name
				</span>
				<span className='text-base font-semibold'>{auditName || '-'}</span>
			</div>

			<div className='flex gap-3'>
				{/* One tile per fact. Stores and audits are the same number, so
					showing both twice just read as two figures that disagree. */}
				<StatTile
					icon={Store}
					value={selectedStores.length}
					label={`store${
						selectedStores.length === 1 ? '' : 's'
					} (one audit each)`}
				/>
				<StatTile
					icon={Package}
					value={selectedAssetTypes.length}
					label={`asset type${selectedAssetTypes.length === 1 ? '' : 's'} each`}
					tone='teal'
				/>
			</div>

			<div className='px-3 py-2 text-xs border rounded-md text-amber-900 bg-amber-50 border-amber-200'>
				One audit per store. Expected quantities are frozen the moment you
				confirm, and each store is blocked from asset movement until its audit
				is completed or cancelled.
			</div>

			<div className='grid flex-1 min-h-0 grid-cols-2 gap-4'>
				<div className='flex flex-col min-h-0 gap-2'>
					<div className='flex items-center justify-between'>
						<span className='text-xs font-semibold tracking-wide text-gray-500 uppercase'>
							Stores
						</span>
						<span className='text-xs text-gray-400'>
							{selectedStores.length}
						</span>
					</div>
					<div className='flex flex-wrap content-start flex-1 min-h-0 gap-2 p-2 overflow-y-auto border rounded-lg bg-gray-50'>
						{selectedStores.map((store) => (
							<span
								key={store.id}
								className='px-2 py-1 text-xs bg-white border rounded-full shadow-sm h-fit'
							>
								{store.name}
							</span>
						))}
					</div>
				</div>

				<div className='flex flex-col min-h-0 gap-2'>
					<div className='flex items-center justify-between'>
						<span className='text-xs font-semibold tracking-wide text-gray-500 uppercase'>
							Asset Types
						</span>
						<span className='flex items-center gap-2 text-[10px]'>
							<span className='flex items-center gap-1 px-2 py-[2px] rounded-full bg-slate-100 text-slate-700'>
								<ScanLine className='size-3' />
								{scannedCount} scanned (QR)
							</span>
							<span className='flex items-center gap-1 px-2 py-[2px] rounded-full bg-slate-100 text-slate-700'>
								<Hash className='size-3' />
								{countedCount} counted (Non-QR)
							</span>
						</span>
					</div>
					<div className='flex-1 min-h-0 overflow-y-auto border rounded-lg bg-gray-50'>
						{groupedTypes.map((group) => (
							<div key={group.name}>
								<div className='px-3 py-1 text-[10px] font-semibold tracking-wide text-gray-500 uppercase border-b bg-gray-100'>
									{group.name}
									<span className='ml-1 font-normal normal-case'>
										({group.types.length})
									</span>
								</div>
								{group.types.map((type) => (
									<div
										key={type.id}
										className='flex items-center justify-between min-w-0 gap-2 px-3 py-1 bg-white border-b last:border-b-0'
									>
										<span className='min-w-0 text-sm truncate' title={type.name}>
											{type.name}
										</span>
										<span className='inline-flex items-center gap-1 text-[10px] px-2 py-[2px] rounded-full whitespace-nowrap bg-slate-100 text-slate-700'>
											{type.traceable ? (
												<ScanLine className='size-3' />
											) : (
												<Hash className='size-3' />
											)}
											{type.traceable
												? 'Scanned (QR)'
												: 'Counted (Non-QR)'}
										</span>
									</div>
								))}
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default AssetAuditStep4;
